import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { buildPathWithParams, useQueryString } from "../../hooks";
import {
  useAllLocationsQuery,
  useLocationQuery,
  usePartnerCatalogQueryMaybe,
} from "../../offering/api";
import {
  useCurrentQueryString,
  useSyncQueryStringV2,
} from "../../offering/offering_helpers";
import {
  normalizeMembershipOrderParams,
  toQuoteInput,
  useMembershipOrderParamsHandlers,
} from "../../offering/order_params";
import { PartnerConfig } from "../../offering/partner_config";
import {
  MembershipCheckoutBoxDesktopV2,
  MembershipCheckoutBoxMobile,
} from "../components";
import { useMembershipStandardizeImages } from "../../data/image";
import {
  findIsSoldOut,
  getMembershipInfoFromLocation,
  getMembershipLocations,
  getMembershipOfferingLocationFromLocationId,
  hasFoundOfferingFromQueryLocationId,
  isGeoMarketOrSpacePartner,
  shouldHideLocationAndCityPicker,
} from "../../data/partner_offering";
import { useQuoteQuery } from "../../offering/hooks/use_quote_query";
import { useGetPartnerOffering } from "../../data/api";
import MembershipOfferingDetailsBaseV2 from "./template_offering_details_membership_base_v2";
import { useTrackMembershipViewedEcommerceEventOnMount } from "../../offering/analytics_ecommerce_events";
import { setQuoteIDToSession } from "@/lib/js_utils/session_storage_utils";
import { useAppendMembershipCityFromURLLocation } from "../hooks/use_append_city_from_url_location_id";

interface OfferingDetailsMembershipParams {
  offeringID: string;
}

export interface OfferingDetailsMembershipQueryParams {
  locationID?: string;
  fromCatalog?: string;
  promoCode?: string;
  subscriptionStartDate?: string;
  fromCategory?: string;
}

interface OfferingDetailsMembershipV2Props {
  partnerConfig: PartnerConfig;
  showCheckoutPrice?: boolean;
}

export function OfferingDetailsMembershipV2(
  props: OfferingDetailsMembershipV2Props
) {
  const { showCheckoutPrice = true, partnerConfig } = props;
  const pageParams = useParams<OfferingDetailsMembershipParams>();
  const queryParams = useQueryString<OfferingDetailsMembershipQueryParams>();
  const pageParamsOfferingID = pageParams.offeringID;
  const offeringID = pageParamsOfferingID;
  const partnerOffering = useGetPartnerOffering(offeringID);

  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryParams.fromCatalog
  );

  const membershipLocations = getMembershipLocations(
    partnerOffering.offeringLocations
  );

  const history = useHistory();

  const showStartDate = partnerOffering.allowSelectableStartDate;

  const {
    orderParams,
    orderParamsError,
    handleChangeLocation,
    handleChangeCity,
    handleChangePromoCode,
    handleChangeStartDate,
  } = useMembershipOrderParamsHandlers(showStartDate, {
    ...normalizeMembershipOrderParams(
      {
        ...queryParams,
        promoCode: queryParams.promoCode,
        locationID: queryParams.locationID,
        subscriptionStartDate: queryParams.subscriptionStartDate,
      },
      showStartDate
    ),
  });

  const hasNoOfferingErrorFromQueryLocationId = useMemo(
    () =>
      hasFoundOfferingFromQueryLocationId(
        partnerOffering.offeringLocations,
        orderParams.locationID
      ) ||
      !orderParams.locationID ||
      !partnerOffering.offeringLocations,
    [partnerOffering.offeringLocations, orderParams.locationID]
  );

  const hideLocationAndCityPicker = useMemo(
    () =>
      shouldHideLocationAndCityPicker(partnerOffering.offeringLocations) &&
      hasNoOfferingErrorFromQueryLocationId,
    [partnerOffering.offeringLocations, hasNoOfferingErrorFromQueryLocationId]
  );

  useAppendMembershipCityFromURLLocation({
    offeringLocations: partnerOffering.offeringLocations,
    isCityPickerHidden: shouldHideLocationAndCityPicker(
      partnerOffering.offeringLocations
    ),
    handleChangeCity,
    handleChangeLocation,
  });

  const selectedLocationQuery = useLocationQuery(orderParams.locationID);

  const { data: location } = selectedLocationQuery;

  const selectedMembership = useMemo(
    () =>
      getMembershipInfoFromLocation(
        partnerOffering.offeringLocations,
        orderParams.locationID
      ),
    [orderParams.locationID, partnerOffering.offeringLocations]
  );

  const offeringLocation = useMemo(
    () =>
      getMembershipOfferingLocationFromLocationId(
        partnerOffering.offeringLocations,
        orderParams.locationID
      ),
    [orderParams.locationID, partnerOffering.offeringLocations]
  );

  const locatableType =
    offeringLocation?.membership?.locatableType || "Location";

  const allLocations = useAllLocationsQuery(
    locatableType === "SpacePartner" ? partnerOffering.partner.id : undefined
  );

  useEffect(() => {
    if (partnerCatalog?.promoCode && !queryParams.promoCode) {
      handleChangePromoCode(partnerCatalog?.promoCode);
    }
  }, [handleChangePromoCode, partnerCatalog, queryParams.promoCode]);

  const quoteInput = useMemo(
    () =>
      (orderParams.locationID || hideLocationAndCityPicker) &&
      hasNoOfferingErrorFromQueryLocationId
        ? toQuoteInput(
            "",
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: "",
              attendees: 1,
            },
            {
              membershipID: selectedMembership?.id || "",
              subscriptionInterval: "monthly",
              subscriptionStartDate: orderParams.subscriptionStartDate,
            }
          )
        : null,
    [
      hideLocationAndCityPicker,
      orderParams,
      partnerOffering.partner.id,
      selectedMembership,
      hasNoOfferingErrorFromQueryLocationId,
    ]
  );

  const { data: quote, error: quoteError } = useQuoteQuery(quoteInput);

  useEffect(() => {
    setQuoteIDToSession(quote?.id, selectedMembership?.id);
  }, [quote?.id, selectedMembership?.id]);

  const queryString = useCurrentQueryString(orderParams);
  useSyncQueryStringV2(orderParams);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/checkout/${pageParamsOfferingID}?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, pageParamsOfferingID, queryString]);

  const handlePressBack = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      queryParams.fromCategory
        ? `/offerings/category/${queryParams.fromCategory}`
        : `/offerings/catalog/${partnerOffering.partner.partnerSlug}`,
      { reset: true, keepOnReset: ["fromCatalog", "promoCode"] }
    );
    history.replace(pathWithParams);
  }, [history, partnerOffering.partner.partnerSlug, queryParams.fromCategory]);

  useEffect(() => {
    if (queryParams.locationID) {
      const pathWithParams = buildPathWithParams(`/offerings/${offeringID}`);
      history.replace(pathWithParams);
    }
  }, [queryParams.locationID, offeringID, history]);

  const shouldUseOffering =
    !location &&
    !shouldHideLocationAndCityPicker(partnerOffering.offeringLocations);

  const images = useMembershipStandardizeImages(
    shouldUseOffering ? [] : selectedMembership?.images || [],
    partnerOffering
  );

  useTrackMembershipViewedEcommerceEventOnMount(
    partnerOffering,
    selectedMembership,
    quote
  );

  const locations =
    hideLocationAndCityPicker &&
    locatableType === "SpacePartner" &&
    allLocations.data
      ? allLocations.data
      : membershipLocations;

  const allSoldOut = findIsSoldOut(
    hideLocationAndCityPicker ? [] : locations,
    partnerOffering.offeringLocations
  );

  useEffect(() => {
    if (
      locations.length === 1 &&
      !orderParams.locationID &&
      !hideLocationAndCityPicker
    ) {
      handleChangeCity(locations[0].city);
      handleChangeLocation(locations[0].id);
    }
  }, [
    handleChangeCity,
    handleChangeLocation,
    hideLocationAndCityPicker,
    locations,
    orderParams.locationID,
  ]);

  const checkoutBox = (
    <MembershipCheckoutBoxDesktopV2
      orderParamsErrors={orderParamsError}
      orderParams={orderParams}
      onChangeLocation={handleChangeLocation}
      onChangeCity={handleChangeCity}
      onContinue={handleContinue}
      locations={locations}
      location={location}
      quote={quote ? quote : undefined}
      datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
      quoteLoading={false}
      showCheckoutPrice={showCheckoutPrice}
      showDiscountLabel={partnerOffering.showDiscountLabel}
      showPriceInfoMessage={false}
      selectedMembership={selectedMembership}
      offeringLocations={partnerOffering.offeringLocations}
      onChangeStartDate={handleChangeStartDate}
      showStartDate={showStartDate}
      quoteError={quoteError}
      soldOut={
        hideLocationAndCityPicker
          ? selectedMembership?.inventory === 0
          : !!location && selectedMembership?.inventory === 0
      }
    />
  );

  return (
    <MembershipOfferingDetailsBaseV2
      spacePartnerOrGeoMarket={isGeoMarketOrSpacePartner(
        partnerOffering.offeringLocations
      )}
      partnerOffering={partnerOffering}
      partnerOfferingID={offeringID}
      partnerConfig={partnerConfig}
      onPressBack={handlePressBack}
      data={{
        ...images,
        offeringName: partnerOffering.offeringTitle || "",
        location: location,
        locations,
      }}
      checkoutBoxDesktop={checkoutBox}
      checkoutBoxMobile={
        <MembershipCheckoutBoxMobile
          checkoutBox={checkoutBox}
          membershipDetail={selectedMembership}
          showPrice={!!location || hideLocationAndCityPicker || allSoldOut}
          onContinue={handleContinue}
          allSoldOut={allSoldOut}
          hideAllPickers={
            hideLocationAndCityPicker ||
            (locations.length === 1 && !showStartDate)
          }
        />
      }
    />
  );
}
